'use client';
/* eslint-disable jsx-a11y/no-redundant-roles, jsx-a11y/no-interactive-element-to-noninteractive-role --
   The table is laid out with `display: grid` (CMS-1772 R2), and Chrome and
   WebKit drop the native table semantics whenever `display` is overridden. The
   role below looks redundant against the markup but is what keeps the table in
   the accessibility tree, so it must stay. */
/* eslint-disable tailwindcss/no-arbitrary-value */

import classNames from 'classnames';
import { useState } from 'react';
import { FeLink } from '../../../components/FeLink';
import { RichText } from '../../../components/RichText';
import { useIsTruncated } from '../../../utils/useIsTruncated';
import { CELL_PADDING } from '../constants';
import { TableBuilderValueCell } from '../types';

interface TextCellProps {
  cell: TableBuilderValueCell;
  heightVariant: 'COMPACT' | 'FLEXIBLE';
  showMoreLabel: string;
  showLessLabel: string;
}

/**
 * The default value cell: authored rich text, optionally with a link and an
 * expand/collapse control. Also the fallback for a cell no other branch claims,
 * hence `cell.text ?? ''` rather than required text.
 *
 * The expansion state and truncation probe live here, not in `ValueCell`: they
 * belong to this cell alone, and hooks cannot sit behind a dispatch branch.
 */
export const TextCell = ({
  cell,
  heightVariant,
  showMoreLabel,
  showLessLabel,
}: TextCellProps) => {
  const [expanded, setExpanded] = useState(false);
  const isExpandable = cell.freeTextType === 'LONG_TEXT_EXPAND_COLLAPSE';
  const isCompact = heightVariant === 'COMPACT';
  const [textRef, isTruncated] = useIsTruncated({ enabled: !expanded });

  return (
    // Column width comes from the table's grid tracks (CMS-1772 R2), so the cell
    // itself carries none — `break-words` keeps an unbroken string (a bare URL)
    // wrapping inside the track instead of widening it (R3).
    //
    // `overflow-hidden` covers what `break-words` cannot. `cell.text` is authored
    // HTML rendered through `RichText`, so it can hold an `<img>` or `<iframe>`,
    // and a grid item in a definite-width track resolves `min-width: auto` to
    // zero — the track cannot grow to fit, so an intrinsically wider embed would
    // otherwise paint outside its column. The cell's height is content-driven, so
    // nothing is clipped vertically.
    <td
      role="cell"
      className={classNames(
        'bg-fill/card group-hover/row:bg-fill/base',
        'border-l border-t border-borders/default',
        'min-h-table/cell-min-height',
        CELL_PADDING,
        'break-words overflow-hidden',
      )}
    >
      <div className="flex h-full flex-col justify-center gap-spacing/gap/soft-8px">
        <div
          ref={textRef}
          className={classNames({
            'line-clamp-2': isCompact && !isExpandable,
            'line-clamp-4': isExpandable && !expanded,
          })}
        >
          <RichText
            className={classNames(
              'font-font-weight/table-cell-normal text-font-size/table-cell leading-line-height/table-cell',
              'text-text/primary',
            )}
            baseTextStyle="none"
          >
            {cell.text ?? ''}
          </RichText>
          {isExpandable && cell.link && (
            <FeLink
              href={cell.link.url}
              className={classNames(
                'text-fill/primary',
                'font-font-weight/table-cell-normal text-font-size/table-cell leading-line-height/table-cell',
              )}
            >
              {cell.link.label}
            </FeLink>
          )}
        </div>
        {isExpandable && isTruncated && (
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded(!expanded)}
            className={classNames(
              'text-left',
              'font-font-weight/medium text-font-size/table-cell leading-line-height/table-cell',
              'text-text/primary underline cursor-pointer',
            )}
          >
            {expanded ? showLessLabel : showMoreLabel}
          </button>
        )}
        {!isExpandable && cell.link && (
          <FeLink
            href={cell.link.url}
            className={classNames(
              'text-fill/primary',
              'font-font-weight/table-cell-normal text-font-size/table-cell leading-line-height/table-cell',
            )}
          >
            {cell.link.label}
          </FeLink>
        )}
      </div>
    </td>
  );
};
